import { db } from '@centered/firebase'
import { useEffect, useMemo, useState } from 'react'

import Masonry from 'react-masonry-css'
import { TwitterTweetEmbed } from 'react-twitter-embed'

interface SocialCardProps
  extends React.ComponentProps<typeof TwitterTweetEmbed> {
  depth: number
}

function SocialCard({
  depth = 1,
  ...embedProps
}: SocialCardProps): JSX.Element {
  const depthClass = useMemo(() => {
    switch (depth) {
      case 1:
        return 'shadow-xl'
      case 2:
        return 'shadow-2xl'
    }
  }, [depth])

  return (
    <div className={`w-full  ${depthClass}`}>
      <TwitterTweetEmbed {...embedProps} />
    </div>
  )
}

const startingCardsCount = 6

export default function SocialCards(): JSX.Element {
  const [ids, setIds] = useState<string[]>([])
  const [loadedCount, setLoadedCount] = useState(0)

  const onLoad = () => setLoadedCount((previous) => previous + 1)

  useEffect(() => {
    const unsubscribe = db.doc(`website/quotes`).onSnapshot((doc) => {
      const quotes: string[] = doc.data()?.quotes ?? []

      const ids = quotes
        .map((quote) => {
          const url = new URL(quote)

          return url.pathname.split('/').pop() ?? ''
        })
        .filter((id) => id.length > 0)

      setIds(ids)
    })

    return () => {
      unsubscribe()
    }
  }, [])

  return (
    <div className="w-full">
      <style>{`
        .twitter-tweet {
          margin-top: 0 !important;
          margin-bottom: 1rem !important;
        }

        .my-masonry-grid {
          display: -webkit-box; /* Not needed if autoprefixing */
          display: -ms-flexbox; /* Not needed if autoprefixing */
          display: flex;
          margin-left: -30px; /* gutter size offset */
          width: auto;
        }
        .my-masonry-grid_column {
          padding-left: 30px; /* gutter size */
          background-clip: padding-box;
        }
        
        /* Style your items */
        .my-masonry-grid_column > div { /* change div to reference your elements you put in <Masonry> */
          margin-bottom: 30px;
        }
      `}</style>

      <Masonry
        breakpointCols={{
          default: 3,
          700: 2,
          500: 1,
        }}
        className="my-masonry-grid"
        columnClassName="my-masonry-grid_column"
      >
        {ids
          // We exponentially increase the number of cards shown as they load in
          // This way we don't load potentially dozens of <iframe> embeds at once
          .slice(0, startingCardsCount + loadedCount * 1.5)
          .map((id) => (
            <SocialCard
              key={id}
              tweetId={id}
              onLoad={onLoad}
              depth={Math.floor(Math.random() * 2) + 1}
            />
          ))}
      </Masonry>
    </div>
  )
}
